import { forwardRef, type HTMLAttributes } from 'react'
import { cn } from '@/lib/utils.ts'

/**
 * Standard-Container fuer Inhalts-Bloecke. Gekoppelt mit `Card.Header`,
 * `Card.Body`, `Card.Footer` – kann aber auch direkt als Wrapper verwendet werden.
 */
const Card = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'bg-white border border-gray-200 rounded-lg shadow-card overflow-hidden',
        className,
      )}
      {...props}
    />
  ),
)
Card.displayName = 'Card'

const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-3',
        className,
      )}
      {...props}
    />
  ),
)
CardHeader.displayName = 'Card.Header'

const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3 ref={ref} className={cn('text-h4 text-gray-900', className)} {...props} />
  ),
)
CardTitle.displayName = 'Card.Title'

const CardBody = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('p-4', className)} {...props} />
  ),
)
CardBody.displayName = 'Card.Body'

const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-2',
        className,
      )}
      {...props}
    />
  ),
)
CardFooter.displayName = 'Card.Footer'

const CompoundCard = Object.assign(Card, {
  Header: CardHeader,
  Title: CardTitle,
  Body: CardBody,
  Footer: CardFooter,
})

export { CompoundCard as Card }
